import { ConcurrencySlotsModal } from '@/components/admin/ConcurrencySlotsModal';
import { EndpointFormModal } from '@/components/admin/EndpointFormModal';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import {
  type LlmConcurrencyRow,
  type LlmEndpoint,
  type Provider,
  providerSupportsExtendedThinking,
} from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Check, CheckCircle2, ChevronDown, MoreHorizontal, Plus, XCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { formatRelative } from './EndpointDetail';

type ModalState = { kind: 'create' } | { kind: 'edit'; endpoint: LlmEndpoint } | null;

type StatusFilter = 'all' | 'enabled' | 'disabled';
type CapFilter = 'general' | 'vision' | 'audio' | 'embedding' | 'image';

const CAP_LABELS: Record<CapFilter, string> = {
  general: '일반',
  vision: 'Vision',
  audio: 'Audio',
  embedding: 'Embedding',
  image: 'Gen Image',
};
const CAP_ORDER: CapFilter[] = ['general', 'vision', 'audio', 'embedding', 'image'];

/** 특수 capability 플래그가 하나도 없는 순수 챗 엔드포인트. */
function isGeneralEndpoint(e: LlmEndpoint): boolean {
  return (
    !e.supportsVision && !e.supportsAudio && !e.supportsEmbedding && !e.supportsImageGeneration
  );
}

function endpointMatchesCap(e: LlmEndpoint, cap: CapFilter): boolean {
  switch (cap) {
    case 'general':
      return isGeneralEndpoint(e);
    case 'vision':
      return e.supportsVision;
    case 'audio':
      return e.supportsAudio;
    case 'embedding':
      return e.supportsEmbedding;
    case 'image':
      return e.supportsImageGeneration;
  }
}

const PROVIDER_LABELS: Record<Provider, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  gemini: 'Gemini',
  gcp: 'GCP Vertex AI (Gemini)',
  'gcp-anthropic': 'GCP Vertex AI (Claude)',
  vllm: 'vLLM',
  mlx: 'mlx',
  ollama: 'Ollama',
  'lm-studio': 'LM Studio',
  'openai-compatible': 'OpenAI 호환',
};

export function AdminLlmEndpoints() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [modal, setModal] = useState<ModalState>(null);
  // 슬롯 상세 모달: null=닫힘, {endpointId}=열림 (endpointId null 이면 전체 보기).
  const [slotsModal, setSlotsModal] = useState<{ endpointId: string | null } | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [capFilter, setCapFilter] = useState<Set<CapFilter>>(new Set());
  const [probeResult, setProbeResult] = useState<{
    id: string;
    ok: boolean;
    models: string[];
    error?: string;
  } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'llm-endpoints'],
    queryFn: () => api.adminLlmList(),
    refetchInterval: 30_000,
    refetchOnMount: 'always',
  });

  // 페이지 진입 즉시 + 30초마다 모든 엔드포인트 probe → DB 의 last_probe_* 갱신.
  useQuery({
    queryKey: ['admin', 'llm-endpoints', 'probe-all'],
    queryFn: async () => {
      await api.adminLlmProbeAll();
      qc.invalidateQueries({ queryKey: ['admin', 'llm-endpoints'], exact: true });
      return Date.now();
    },
    refetchInterval: 30_000,
    refetchOnMount: 'always',
    staleTime: 0,
  });

  // 동시성 슬롯 현황 — 실시간 모니터링이라 짧은 주기(5초)로 폴링.
  const { data: concData } = useQuery({
    queryKey: ['admin', 'llm-concurrency'],
    queryFn: () => api.adminLlmConcurrency(),
    refetchInterval: 5_000,
    refetchOnMount: 'always',
  });
  const concByEndpoint = new Map<string, LlmConcurrencyRow>(
    (concData?.rows ?? []).map((r) => [r.endpointId, r]),
  );

  const sweepMut = useMutation({
    mutationFn: () => api.adminLlmConcurrencySweep(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'llm-concurrency'] }),
    onError: (err: unknown) => {
      setServerError(err instanceof ApiError ? err.message : '슬롯 정리에 실패했습니다.');
    },
  });

  const updateMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof api.adminLlmUpdate>[1] }) =>
      api.adminLlmUpdate(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'llm-endpoints'] }),
    onError: (err: unknown) => {
      setServerError(err instanceof ApiError ? err.message : '수정에 실패했습니다.');
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.adminLlmDelete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'llm-endpoints'] }),
    onError: (err: unknown) => {
      setServerError(err instanceof ApiError ? err.message : '삭제에 실패했습니다.');
    },
  });

  const probeMut = useMutation({
    mutationFn: (id: string) => api.adminLlmProbe(id).then((r) => ({ ...r, id })),
    onSuccess: (r) => setProbeResult(r),
    onError: (err: unknown) => {
      setServerError(err instanceof ApiError ? err.message : '연결 테스트 실패.');
    },
  });

  const endpoints = data?.endpoints ?? [];
  const filtered = endpoints.filter((e) => {
    if (statusFilter === 'enabled' && !e.isEnabledForUsers) return false;
    if (statusFilter === 'disabled' && e.isEnabledForUsers) return false;
    if (capFilter.size > 0 && ![...capFilter].some((c) => endpointMatchesCap(e, c))) return false;
    return true;
  });
  const isFiltered = statusFilter !== 'all' || capFilter.size > 0;

  return (
    <PageShell
      header={
        <PageHeader
          title="LLM 엔드포인트"
          count={isFiltered ? filtered.length : endpoints.length}
          description="사용자가 사용할 모델을 여기서 관리합니다. provider 별 고유 SDK 로 연결되며, 기본 모델 1개와 사용자 노출 여부를 지정합니다."
          actions={
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setSlotsModal({ endpointId: null })}
                title="현재 점유 중인 슬롯을 점유자/획득/만료 단위로 보고, 안 풀리는 슬롯을 강제 해제합니다."
              >
                슬롯 현황 상세
              </Button>
              <Button
                variant="outline"
                onClick={() => sweepMut.mutate()}
                disabled={sweepMut.isPending}
                title="만료됐지만 아직 회수되지 않은 슬롯을 즉시 정리합니다 (매분 자동 정리도 동작)."
              >
                {sweepMut.isPending ? '정리 중…' : '스테일 슬롯 정리'}
              </Button>
              <Button onClick={() => setModal({ kind: 'create' })}>
                <Plus className="size-4" />
                엔드포인트 추가
              </Button>
            </div>
          }
        />
      }
    >
      {serverError ? (
        <div
          role="alert"
          className="mb-4 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {serverError}
        </div>
      ) : null}

      {probeResult ? (
        // biome-ignore lint/a11y/useSemanticElements: <output> 태그는 form 컨텍스트 의미가 강해 일반 status 표시에는 div 가 적합
        <div
          role="status"
          className={cn(
            'mb-4 rounded-md border px-3 py-2 text-sm',
            probeResult.ok
              ? 'border-success/30 bg-success/5 text-success'
              : 'border-destructive/30 bg-destructive/5 text-destructive',
          )}
        >
          {probeResult.ok
            ? `연결 성공 — 모델 ${probeResult.models.length}개 발견`
            : `연결 실패: ${probeResult.error ?? '알 수 없는 오류'}`}
          <button
            type="button"
            onClick={() => setProbeResult(null)}
            className="ml-3 text-xs underline"
          >
            닫기
          </button>
        </div>
      ) : null}

      {isLoading ? (
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      ) : error ? (
        <p className="text-sm text-destructive">
          {error instanceof ApiError ? error.message : '엔드포인트 목록을 불러오지 못했습니다.'}
        </p>
      ) : (
        <>
          <FilterBar
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            capFilter={capFilter}
            onCapChange={setCapFilter}
            total={endpoints.length}
            shown={filtered.length}
          />
          <div className="rounded-2xl border border-border bg-surface overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-surface-muted/40 text-xs uppercase tracking-wider text-foreground-muted">
                <tr>
                  <Th>이름</Th>
                  <Th>모델</Th>
                  <Th>릴레이 alias</Th>
                  <Th>Base URL</Th>
                  <Th>상태</Th>
                  <Th>동시 사용</Th>
                  <Th>플래그</Th>
                  <Th align="right">액션</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => (
                  <Row
                    key={e.id}
                    endpoint={e}
                    conc={concByEndpoint.get(e.id)}
                    onOpen={() =>
                      navigate({ to: '/admin/llm/endpoints/$id', params: { id: e.id } })
                    }
                    onOpenSlots={() => setSlotsModal({ endpointId: e.id })}
                    onEdit={() => setModal({ kind: 'edit', endpoint: e })}
                    onProbe={() => probeMut.mutate(e.id)}
                    onToggleDefault={() =>
                      updateMut.mutate({ id: e.id, body: { isDefault: !e.isDefault } })
                    }
                    onToggleEnabled={() =>
                      updateMut.mutate({
                        id: e.id,
                        body: { isEnabledForUsers: !e.isEnabledForUsers },
                      })
                    }
                    onDelete={() => {
                      if (window.confirm(`엔드포인트 ${e.name} 을 삭제할까요?`)) {
                        deleteMut.mutate(e.id);
                      }
                    }}
                  />
                ))}
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center text-foreground-muted py-10">
                      {endpoints.length === 0
                        ? '등록된 엔드포인트가 없습니다.'
                        : '조건에 맞는 엔드포인트가 없습니다.'}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </>
      )}

      <EndpointFormModal open={!!modal} mode={modal} onClose={() => setModal(null)} />
      <ConcurrencySlotsModal
        open={!!slotsModal}
        onClose={() => setSlotsModal(null)}
        endpoints={endpoints}
        filterEndpointId={slotsModal?.endpointId ?? null}
      />
    </PageShell>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <th
      scope="col"
      className={cn('px-4 py-2.5 font-medium', align === 'right' ? 'text-right' : 'text-left')}
    >
      {children}
    </th>
  );
}

function FilterBar({
  statusFilter,
  onStatusChange,
  capFilter,
  onCapChange,
  total,
  shown,
}: {
  statusFilter: StatusFilter;
  onStatusChange: (s: StatusFilter) => void;
  capFilter: Set<CapFilter>;
  onCapChange: (s: Set<CapFilter>) => void;
  total: number;
  shown: number;
}) {
  const statusOpts: Array<{ value: StatusFilter; label: string }> = [
    { value: 'all', label: '전체' },
    { value: 'enabled', label: '활성' },
    { value: 'disabled', label: '비활성' },
  ];
  return (
    <div className="mb-3 flex flex-wrap items-center gap-3">
      <div className="inline-flex rounded-md border border-border bg-surface p-0.5">
        {statusOpts.map((o) => (
          <button
            key={o.value}
            type="button"
            onClick={() => onStatusChange(o.value)}
            className={cn(
              'px-3 h-7 rounded-[5px] text-xs font-medium',
              statusFilter === o.value
                ? 'bg-surface-muted text-foreground'
                : 'text-foreground-muted hover:text-foreground',
            )}
          >
            {o.label}
          </button>
        ))}
      </div>
      <CapabilityDropdown capFilter={capFilter} onCapChange={onCapChange} />
      {(statusFilter !== 'all' || capFilter.size > 0) && (
        <button
          type="button"
          onClick={() => {
            onStatusChange('all');
            onCapChange(new Set());
          }}
          className="text-xs text-foreground-muted underline hover:text-foreground"
        >
          필터 초기화
        </button>
      )}
      <span className="ml-auto text-xs text-foreground-muted">
        {shown} / {total}
      </span>
    </div>
  );
}

function CapabilityDropdown({
  capFilter,
  onCapChange,
}: {
  capFilter: Set<CapFilter>;
  onCapChange: (s: Set<CapFilter>) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [open]);

  const toggle = (cap: CapFilter) => {
    const next = new Set(capFilter);
    if (next.has(cap)) next.delete(cap);
    else next.add(cap);
    onCapChange(next);
  };

  const label =
    capFilter.size === 0
      ? '종류 전체'
      : CAP_ORDER.filter((c) => capFilter.has(c))
          .map((c) => CAP_LABELS[c])
          .join(', ');

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border bg-surface px-3 text-xs text-foreground hover:bg-surface-muted"
      >
        <span className="max-w-[14rem] truncate">{label}</span>
        <ChevronDown className="size-3.5 text-foreground-muted" />
      </button>
      {open ? (
        <div className="absolute left-0 top-full z-50 mt-1 w-44 rounded-md border border-border bg-surface py-1 shadow-lg">
          {CAP_ORDER.map((cap) => {
            const checked = capFilter.has(cap);
            return (
              <button
                key={cap}
                type="button"
                onClick={() => toggle(cap)}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-surface-muted"
              >
                <span
                  className={cn(
                    'inline-grid size-4 place-items-center rounded-sm border',
                    checked ? 'border-accent bg-accent/15 text-accent' : 'border-border',
                  )}
                >
                  {checked ? <Check className="size-3" /> : null}
                </span>
                <span>{CAP_LABELS[cap]}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function Row({
  endpoint,
  conc,
  onOpen,
  onOpenSlots,
  onEdit,
  onProbe,
  onToggleDefault,
  onToggleEnabled,
  onDelete,
}: {
  endpoint: LlmEndpoint;
  conc: LlmConcurrencyRow | undefined;
  onOpen: () => void;
  onOpenSlots: () => void;
  onEdit: () => void;
  onProbe: () => void;
  onToggleDefault: () => void;
  onToggleEnabled: () => void;
  onDelete: () => void;
}) {
  return (
    <tr className="border-t border-border hover:bg-surface-muted/30">
      <td className="px-4 py-3">
        <button type="button" onClick={onOpen} className="text-left hover:underline font-medium">
          {endpoint.displayName ?? endpoint.name}
        </button>
        <div className="mt-0.5 flex items-center gap-2 text-[11px] text-foreground-muted">
          <span className="inline-flex items-center rounded-sm bg-surface-muted px-1.5 h-4 uppercase tracking-wider">
            {PROVIDER_LABELS[endpoint.provider]}
          </span>
          <span className="font-mono">{endpoint.name}</span>
        </div>
      </td>
      <td className="px-4 py-3 font-mono text-xs break-all">{endpoint.model}</td>
      <td className="px-4 py-3 font-mono text-xs">
        {endpoint.relayAlias ? (
          endpoint.relayAlias
        ) : (
          <span className="text-foreground-muted">—</span>
        )}
      </td>
      <td className="px-4 py-3 font-mono text-xs text-foreground-muted max-w-xs truncate">
        {endpoint.baseUrl || '(기본)'}
      </td>
      <td className="px-4 py-3">
        <StatusInline endpoint={endpoint} />
      </td>
      <td className="px-4 py-3">
        <ConcurrencyInline endpoint={endpoint} conc={conc} onOpenSlots={onOpenSlots} />
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap items-center gap-1.5">
          {endpoint.isDefault ? <Badge tone="accent">기본</Badge> : null}
          {endpoint.isEnabledForUsers ? (
            <Badge tone="ok">사용자 노출</Badge>
          ) : (
            <Badge tone="mute">숨김</Badge>
          )}
          {endpoint.extendedThinking && providerSupportsExtendedThinking(endpoint.provider) ? (
            <Badge tone="neutral">사고</Badge>
          ) : null}
          {endpoint.supportsVision ? <Badge tone="neutral">Vision</Badge> : null}
          {endpoint.supportsAudio ? <Badge tone="neutral">Audio</Badge> : null}
          {endpoint.supportsEmbedding ? <Badge tone="neutral">Embedding</Badge> : null}
          {endpoint.supportsImageGeneration ? <Badge tone="neutral">이미지생성</Badge> : null}
        </div>
      </td>
      <td className="px-4 py-3 text-right">
        <RowMenu
          isDefault={endpoint.isDefault}
          isEnabled={endpoint.isEnabledForUsers}
          onEdit={onEdit}
          onProbe={onProbe}
          onToggleDefault={onToggleDefault}
          onToggleEnabled={onToggleEnabled}
          onDelete={onDelete}
        />
      </td>
    </tr>
  );
}

function StatusInline({ endpoint }: { endpoint: LlmEndpoint }) {
  if (endpoint.lastProbedAt === null) {
    return <span className="text-xs text-foreground-muted">—</span>;
  }
  if (endpoint.lastProbeOk === true) {
    // skipped 케이스: 외부 LLM 자동 probe 생략 (modelCount === null + ok)
    if (endpoint.lastProbeModelCount === null) {
      return (
        <span className="inline-flex items-center gap-1.5 text-xs text-foreground-muted">
          외부 — 자가체크 생략
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 text-xs">
        <CheckCircle2 className="size-3.5 text-success" />
        <span>{endpoint.lastProbeModelCount}개</span>
        <span className="text-foreground-muted">{formatRelative(endpoint.lastProbedAt)}</span>
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs"
      title={endpoint.lastProbeError ?? undefined}
    >
      <XCircle className="size-3.5 text-destructive" />
      <span className="text-destructive">실패</span>
      <span className="text-foreground-muted">{formatRelative(endpoint.lastProbedAt)}</span>
    </span>
  );
}

function ConcurrencyInline({
  endpoint,
  conc,
  onOpenSlots,
}: {
  endpoint: LlmEndpoint;
  conc: LlmConcurrencyRow | undefined;
  onOpenSlots: () => void;
}) {
  // maxConcurrent null/0 = 게이트 비활성 (무제한).
  if (endpoint.maxConcurrent === null || endpoint.maxConcurrent <= 0) {
    return <span className="text-xs text-foreground-muted">무제한</span>;
  }
  const activeUser = conc?.activeUser ?? 0;
  const activeSystem = conc?.activeSystem ?? 0;
  const stale = conc?.stale ?? 0;
  const active = activeUser + activeSystem;
  const atCap = active >= endpoint.maxConcurrent;
  return (
    // 클릭 시 슬롯 단위 상세 모달 (해당 엔드포인트 필터). 점유 0 이어도 만료 잔여 진단 위해 항상 열림.
    <button
      type="button"
      onClick={onOpenSlots}
      title="슬롯 상세 보기 / 강제 해제"
      className="text-left text-xs leading-tight hover:underline"
    >
      <span>
        <span className={cn('font-medium', atCap ? 'text-destructive' : 'text-foreground')}>
          {active}
        </span>
        <span className="text-foreground-muted"> / {endpoint.maxConcurrent}</span>
      </span>
      {active > 0 ? (
        <div className="text-[11px] text-foreground-muted">
          사용자 {activeUser} · 시스템 {activeSystem}
        </div>
      ) : null}
      {conc?.oldestAcquiredAt ? (
        <div className="text-[11px] text-foreground-muted">
          최장 점유 {formatRelative(conc.oldestAcquiredAt)}
        </div>
      ) : null}
      {stale > 0 ? <div className="text-[11px] text-destructive">정리 대기 {stale}</div> : null}
    </button>
  );
}

function Badge({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone: 'accent' | 'ok' | 'mute' | 'neutral';
}) {
  const cls = {
    accent: 'bg-accent/15 text-accent',
    ok: 'bg-success/15 text-success',
    mute: 'bg-surface-muted text-foreground-muted',
    neutral: 'border border-border text-foreground-muted',
  }[tone];
  return (
    <span
      className={cn(
        'inline-flex items-center px-2 h-5 rounded-sm text-[10px] uppercase tracking-wider',
        cls,
      )}
    >
      {children}
    </span>
  );
}

function RowMenu({
  isDefault,
  isEnabled,
  onEdit,
  onProbe,
  onToggleDefault,
  onToggleEnabled,
  onDelete,
}: {
  isDefault: boolean;
  isEnabled: boolean;
  onEdit: () => void;
  onProbe: () => void;
  onToggleDefault: () => void;
  onToggleEnabled: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (btnRef.current?.contains(e.target as Node)) return;
      if (menuRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onScrollOrResize = () => setOpen(false);
    window.addEventListener('mousedown', handler);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('mousedown', handler);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  const toggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    }
    setOpen((v) => !v);
  };

  const close = () => setOpen(false);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label="액션 메뉴"
        onClick={toggle}
        className="size-8 inline-grid place-items-center rounded-md text-foreground-muted hover:bg-surface-muted hover:text-foreground"
      >
        <MoreHorizontal className="size-4" />
      </button>
      {open && pos
        ? createPortal(
            <div
              ref={menuRef}
              style={{ position: 'fixed', top: pos.top, right: pos.right }}
              className="w-56 z-50 rounded-md border border-border bg-surface text-foreground shadow-lg py-1 text-sm text-left"
            >
              <MenuItem
                onClick={() => {
                  close();
                  onEdit();
                }}
              >
                편집
              </MenuItem>
              <MenuItem
                onClick={() => {
                  close();
                  onProbe();
                }}
              >
                연결 테스트
              </MenuItem>
              <MenuItem
                disabled={isDefault}
                onClick={() => {
                  close();
                  onToggleDefault();
                }}
              >
                {isDefault ? '기본 모델 (현재)' : '기본 모델로 지정'}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  close();
                  onToggleEnabled();
                }}
              >
                {isEnabled ? '사용자 노출 해제' : '사용자에게 노출'}
              </MenuItem>
              <div className="my-1 border-t border-border" />
              <MenuItem
                danger
                onClick={() => {
                  close();
                  onDelete();
                }}
              >
                삭제
              </MenuItem>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function MenuItem({
  children,
  onClick,
  disabled,
  danger,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'block w-full text-left px-3 py-1.5 text-sm hover:bg-surface-muted',
        'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent',
        danger ? 'text-destructive' : 'text-foreground',
      )}
    >
      {children}
    </button>
  );
}
